import * as Yup from 'yup'
import {useTranslation} from 'react-i18next'
import {camelize} from 'humps'
import {useRails} from '~/react/contexts/rails'
import usePaymentMethods from '~/react/hooks/usePaymentMethods'
import {MAXIMUM_REFERENCE_LENGTH} from '../constants'

const useFormSchema = (payable) => {
  const {t} = useTranslation('pointsOnPayments')
  const maxDueAmount = payable.dueAmount.getAmount()

  const {
    organisation: {
      currentSupplier: {id: supplierId},
    },
  } = useRails()

  const {findPaymentMethod} = usePaymentMethods(supplierId)

  const findAndParsePaymentMethod = (value) => {
    const paymentMethod = findPaymentMethod(value)

    if (!paymentMethod) return {}

    return paymentMethod[camelize(paymentMethod.type)]
  }

  Yup.setLocale({
    mixed: {
      required: t('common:yup.mixed.required'),
    },
    // use functions to generate an error object that includes the value from the schema
    string: {
      min: ({min}) => t('common:yup.string.min', {min}),
      max: ({max}) => t('common:yup.string.max', {max}),
      email: () => t('common:yup.string.email'),
    },
  })

  return Yup.object().shape({
    amount: Yup.number()
      .min(
        1_00,
        t(
          'pointsOnPayments:paymentWizard.formPanel.paymentDetailsForm.fields.amount.minError',
        ),
      )
      .max(
        maxDueAmount,
        t(
          'pointsOnPayments:paymentWizard.formPanel.paymentDetailsForm.fields.amount.maxError',
          {
            maxDueAmount: (maxDueAmount / 100).toFixed(2),
          },
        ),
      )
      .required(t('fields.amount.requiredError')),
    reference: Yup.string()
      .max(
        MAXIMUM_REFERENCE_LENGTH,
        t(
          'pointsOnPayments:paymentWizard.formPanel.paymentDetailsForm.fields.reference.maxError',
          {
            maxLength: MAXIMUM_REFERENCE_LENGTH,
          },
        ),
      )
      .required(
        t(
          'pointsOnPayments:paymentWizard.formPanel.paymentDetailsForm.fields.reference.requiredError',
        ),
      ),
    paymentMethodId: Yup.string()
      .test({
        name: 'withinPaymentLimit',
        test: (value, {createError}) => {
          const {dailyPaymentCount, dailyPaymentLimit} =
            findAndParsePaymentMethod(value)

          if (!dailyPaymentLimit || !dailyPaymentCount) return true

          return (
            dailyPaymentCount < dailyPaymentLimit ||
            createError({
              message: t(
                'pointsOnPayments:paymentWizard.formPanel.paymentMethodForm.fields.paymentMethodId.limitError',
                {
                  limit: dailyPaymentLimit,
                },
              ),
            })
          )
        },
      })
      .required(
        t(
          'pointsOnPayments:paymentWizard.formPanel.paymentMethodForm.fields.paymentMethodId.requiredError',
        ),
      ),
  })
}

export default useFormSchema
